import {DialogTitle} from '@apps-common/ui-components';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import {bool, func, object} from 'prop-types';
import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {notify} from '@actions/notifications';
import {trackEvent} from '@app/analytics';
import {
  useGetCollectionPropertyNames,
  useUpdateCollection,
} from '@app/api/collections';
import LoadingMessage from '@app/components/common/LoadingMessage';
import {MAX_TITLE_LENGTH} from '@app/constants';
import useCollectionParams from '@app/hooks/useCollectionParams';

const EVENT_NAME = 'Edit Collection Modal';

const EditCollectionModal = ({open, onClose, collection}) => {
  const dispatch = useDispatch();
  const {collectionId, setCollection} = useCollectionParams();

  const [title, setTitle] = useState('');
  const [titleProperty, setTitleProperty] = useState('');
  const [descriptionProperty, setDescriptionProperty] = useState('');
  const [helperText, setHelperText] = useState(false);

  const {mutate: updateCollection, isPending: isUpdatingCollection} =
    useUpdateCollection({
      onSuccess: data => {
        trackEvent(EVENT_NAME, {action: 'Edited Collection'});
        if (collectionId) {
          setCollection(data.id);
        }
        onClose();
      },
      onError: () => {
        dispatch(
          notify({
            message: 'There was an error while updating the collection',
            severity: 'error',
          })
        );
      },
    });

  const {
    data: propNames,
    error,
    isLoading,
  } = useGetCollectionPropertyNames(
    {
      collectionId: collection.id,
    },
    {enabled: open}
  );

  useEffect(() => {
    if (error) {
      const errorMessage = 'There was an error reading the collection';

      dispatch(
        notify({
          message: errorMessage,
          severity: 'error',
          persist: true,
        })
      );
    }
  }, [collectionId, dispatch, error]);

  useEffect(() => {
    if (collection.title) {
      setTitle(collection.title);
    }
    if (collection.title_property) {
      setTitleProperty(collection.title_property);
    }
    if (collection.description_property) {
      setDescriptionProperty(collection.description_property);
    }
  }, [collection]);

  useEffect(() => {
    if (title.length >= MAX_TITLE_LENGTH) {
      setHelperText(true);
    } else {
      setHelperText(false);
    }
  }, [title.length]);

  const onSave = () => {
    updateCollection({
      collectionId: collection.id,
      data: {
        title,
        // normalize 'empty' back to null
        title_property: titleProperty || null,
        description_property: descriptionProperty || null,
      },
    });
  };

  const handleOnClose = () => {
    trackEvent(EVENT_NAME, {
      action: 'closed modal',
    });

    onClose();
  };

  const propertyMenus = () => [
    <MenuItem key="_" value="">
      -- Unset --
    </MenuItem>,
    ...(propNames?.properties ?? []).map(name => (
      <MenuItem key={name} value={name}>
        {name}
      </MenuItem>
    )),
  ];

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleOnClose}>
      <DialogTitle onClose={handleOnClose} title="Edit collection" />
      <DialogContent>
        <Stack spacing={2}>
          <TextField
            fullWidth
            value={title}
            onChange={evt => setTitle(evt.target.value)}
            label="Title"
            helperText={
              helperText
                ? 'The max length of a collection title is 255 characters.'
                : ' '
            }
            error={!!helperText}
            slotProps={{
              htmlInput: {
                maxLength: MAX_TITLE_LENGTH,
                'data-testid': 'edit-title-input',
              },
            }}
          />
          {isLoading ? (
            <LoadingMessage
              progressSize={36}
              titleTypographyProps={{variant: 'h6'}}
            />
          ) : (
            <>
              <TextField
                select
                fullWidth
                value={titleProperty}
                onChange={evt => setTitleProperty(evt.target.value)}
                label="Feature title property"
                helperText="The value of this property will be used to label features."
              >
                {propertyMenus()}
              </TextField>
              <TextField
                select
                fullWidth
                value={descriptionProperty}
                onChange={evt => setDescriptionProperty(evt.target.value)}
                label="Feature description property"
                helperText="The value of this property will be used as additional details."
              >
                {propertyMenus()}
              </TextField>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleOnClose} variant="outlined">
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={
            isUpdatingCollection ||
            (title === collection.title &&
              (titleProperty || null) === collection.title_property &&
              (descriptionProperty || null) === collection.description_property)
          }
          onClick={onSave}
        >
          {isUpdatingCollection ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EditCollectionModal.propTypes = {
  collection: object,
  onClose: func,
  open: bool,
};

export default EditCollectionModal;
